import { Flex, KpiCard, SwipeablePanel } from "@wayflyer/flyui";
import { useIntl } from "react-intl";
import { analytics } from "shared-ui";
import { useAnalyticsContext } from "shared-utils";

import type { SummaryPanelItemProps } from "../../components/summary-panel/summary-panel";
import { MetricReferenceDirection } from "../../components/shared";
import type { OverviewKpiMetric } from "../kpi-data";

export const OverviewMetricCards = ({
  metrics,
  loading,
  referenceText,
  onSelect,
  label,
}: {
  metrics: (SummaryPanelItemProps & OverviewKpiMetric)[];
  loading?: boolean;
  referenceText?: string;
  onSelect?: (metricType: string, selected: boolean) => void;
  label: string;
}) => {
  const intl = useIntl();
  const analyticsContext = useAnalyticsContext();
  return (
    <SwipeablePanel scrollerAccessibilityLabel={label}>
      <Flex gap="4" wrap="nowrap">
        {metrics.map((metric) => {
          const reference = metric.metricReferenceText ?? referenceText ?? "";
          const hasChange = metric.kpi?.change !== undefined;
          const hasReference =
            metric.metricReferenceValue && (hasChange || metric.metricReferenceDirection === undefined);
          const title = metric.kpi?.isRatio
            ? intl.formatMessage(
                { id: "Overview: ratio KPI title", defaultMessage: "{title} (x)" },
                { title: metric.title }
              )
            : metric.title;
          const selectable = onSelect && metric.hasCheckbox !== false;
          const width = metric.kpi?.isRatio ? "360px" : "280px";
          const presentation = {
            title,
            amount: metric.kpi?.amount ?? { value: null },
            trend: hasChange
              ? {
                  value: metric.metricReferenceDirection === MetricReferenceDirection.SAME ? 0 : metric.kpi.change,
                  status: metric.metricReferenceSuccess ? ("positive" as const) : ("critical" as const),
                  iconVariant: "straight" as const,
                  formatFn: () => metric.metricReferenceValue ?? "",
                }
              : undefined,
            infoTooltip: metric.tooltip || undefined,
            isLoading: loading,
            subtext: hasReference
              ? hasChange
                ? reference
                : `${metric.metricReferenceValue} ${reference}`.trim()
              : undefined,
          };
          return (
            <Flex key={metric.metricType} minWidth={width} width={width} shrink={0} direction="column">
              {selectable ? (
                <KpiCard
                  {...presentation}
                  isSelected={metric.selected ?? true}
                  onAction={() => {
                    analytics.fireClickEvent(analyticsContext, { type: "checkbox", buttonValue: metric.metricType });
                    onSelect(metric.metricType, !(metric.selected ?? true));
                  }}
                />
              ) : (
                <KpiCard {...presentation} />
              )}
            </Flex>
          );
        })}
      </Flex>
    </SwipeablePanel>
  );
};
